import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  apiRequest,
  SuccessToast,
  ErrorToast,
  toastConfig,
  devError,
} from "@ridecloud/shared";
import { Toaster } from "react-hot-toast";
import { LoaderInside } from "../components/Loader"; // Import Loader component
import { Mail, Lock, ArrowLeft, ArrowRight, KeyRound } from "lucide-react";
import "../css/ForgotPassword.css";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [token, setToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const handleOtpChange = (index, value) => {
    if (value && !/^\d+$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 3) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      nextInput?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      prevInput?.focus();
    }
  };

  const handleSendOTP = async () => {
    setLoading(true); // Show loader
    try {
      const res = await apiRequest(async (APIv1) =>
        APIv1.post(
          "/auth/send-otp",
          { email, type: "forgot-password" },
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        )
      );

      // const res = { data: { message: "OTP sent successfully!" } }; // Mock response for testing
      // const delay = (ms) => new Promise((res) => setTimeout(res, ms));

      // await delay(5000); // waits for 5 second

      const data = await res.data;
      SuccessToast(data.message || "OTP sent successfully!");
      setOtp(["", "", "", ""]);
      setStep(2);
    } catch (err) {
      ErrorToast(err.response.data.message || "Failed to send OTP");
      devError("Error sending OTP:", err);
    } finally {
      setLoading(false); // Hide loader
    }
  };

  const handleVerifyOtp = async () => {
    setLoading(true); // Show loader

    try {
      const res = await apiRequest(async (APIv1) =>
        APIv1.post(
          "/auth/verify-otp",
          { email, otp: otp.join(""), type: "forgot-password" },
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        )
      );
      setToken(res.data.token); // receive temp token
      SuccessToast(res.data.message || "OTP verified successfully!");
      setOtp(["", "", "", ""]);
      setStep(3);
    } catch (err) {
      ErrorToast(err.response.data.message || "Invalid OTP.");
      devError("Error verifying OTP:", err);
    } finally {
      setLoading(false); // Hide loader
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true); // Show loader
    if (newPassword.length < 8) {
      ErrorToast("Password must be at least 8 characters long");
      return;
    }
    if (!/[A-Z]/.test(newPassword)) {
      ErrorToast("Password must contain at least one uppercase letter");
      return;
    }
    if (!/[a-z]/.test(newPassword)) {
      ErrorToast("Password must contain at least one lowercase letter");
      return;
    }
    if (!/[0-9]/.test(newPassword)) {
      ErrorToast("Password must contain at least one number");
      return;
    }
    if (!/[!@#$%^&*]/.test(newPassword)) {
      ErrorToast("Password must contain at least one special character");
      return;
    }
    if (newPassword.includes(" ")) {
      ErrorToast("Password must not contain spaces");
      return;
    }
    if (newPassword !== confirmPassword) {
      ErrorToast("Passwords do not match");
      return;
    }

    try {
      const res = await apiRequest(async (APIv1) =>
        APIv1.post(
          "/auth/reset-password",
          { token, newPassword },
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        )
      );

      const data = await res.data;
      SuccessToast(data.message || "Password reset successfully!");
      navigate("/login");
    } catch (err) {
      ErrorToast(err.response.data.message || "Failed to reset password");
      devError("Error resetting password:", err);
    } finally {
      setLoading(false); // Hide loader
    }
  };

  return (
    <div className="forgot-password-wrapper">
      <div className="forgot-password-container">
        <div
          className="forgot-back-icon-container"
          onClick={() => navigate("/login")}
        >
          <ArrowLeft className="forgot-back-icon" />
        </div>
        <Toaster {...toastConfig} />

        <div className="forgot-password-header">
          <img
            src="/img/ride_cloud.png"
            alt="icon"
            className="w-14 h-14 sm:w-14 sm:h-14"
          />

          <h2 className="forgot-password-title">Reset Password</h2>
          <p className="forgot-password-subtitle">
            Complete all steps to reset your password
          </p>
        </div>

        <form onSubmit={handleResetPassword} className="forgot-password-form">
          <div className="forgot-password-combined-section">
            {step === 1 && (
              <div className="forgot-password-step-container active">
                <div className="forgot-password-step-number">1</div>
                <div className="forgot-password-step-content">
                  <h3>Email Verification</h3>
                  <p>We'll send you an OTP by email.</p>
                  <div className="forgot-password-input-group">
                    <Mail className="forgot-password-input-icon" />
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Email address"
                      className="forgot-password-input"
                      required
                    />
                  </div>
                  <button
                    type="button"
                    className="forgot-password-section-button"
                    onClick={handleSendOTP}
                  >
                    Send Code
                    {loading ? (
                      <LoaderInside /> // Use Loader component
                    ) : (
                      <>
                        <ArrowRight className="forgot-password-button-icon" />
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="forgot-password-step-container active">
                <div className="forgot-password-step-number">2</div>
                <div className="forgot-password-step-content">
                  <h3>Enter OTP Code</h3>
                  <p>Please enter the OTP sent to your email.</p>
                  <div className="forgot-password-otp-container">
                    {otp.map((digit, index) => (
                      <input
                        key={index}
                        id={`otp-${index}`}
                        type="text"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleOtpChange(index, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(index, e)}
                        className="forgot-password-otp-input"
                        required
                      />
                    ))}
                  </div>
                  <div className="forgot-password-buttons">
                    <button
                      type="button"
                      onClick={() => setStep(1)}
                      className="forgot-password-back-button"
                    >
                      <ArrowLeft className="forgot-password-button-icon" />
                      Back
                    </button>
                    <button
                      type="button"
                      className="forgot-password-section-button"
                      onClick={handleVerifyOtp}
                    >
                      Verify Code
                      {loading ? (
                        <LoaderInside /> // Use Loader component
                      ) : (
                        <>
                          <ArrowRight className="forgot-password-button-icon" />
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="forgot-password-step-container active">
                <div className="forgot-password-step-number">3</div>
                <div className="forgot-password-step-content">
                  <h3>New Password</h3>
                  <p>Enter your new password.</p>

                  <div className="forgot-password-input-group">
                    <Lock className="forgot-password-input-icon" />
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="New password"
                      className="forgot-password-input"
                      required
                    />
                  </div>
                  <div className="forgot-password-input-group">
                    <Lock className="forgot-password-input-icon" />
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm new password"
                      className="forgot-password-input"
                      required
                    />
                  </div>
                  <div className="forgot-password-button-last">
                    <button
                      type="button"
                      onClick={() => setStep(2)}
                      className="forgot-password-back-button"
                    >
                      <ArrowLeft className="forgot-password-button-icon" />
                      Back
                    </button>
                  </div>
                </div>
              </div>
            )}

            {step === 3 && (
              <button type="submit" className="forgot-password-submit-button">
                Reset Password
                {loading ? (
                  <LoaderInside /> // Use Loader component
                ) : (
                  <>
                    <ArrowRight className="forgot-password-button-icon" />
                  </>
                )}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
